import NoRecords from '../../shared/NoRecords';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PaginatedTable from '../PaginatedTable';
import { RefetchBar } from '../../shared/Skeleton';
import CsvButton from '../CsvButton';
import Sparkline from '../Sparkline';
import { ErrorState } from '../../shared/StateMessage';
import { sanitizePanelError } from '../../shared/PanelErrorBoundary';
import { getQueries, getPages, getQueryPages, getSeries } from '../../../api';
import { fmtCount, fmtRatioPct, fmtPosition, pagePath, fmtSearchTerm } from '../lib/format';

/**
 * Fallback page size, used only if a panel arrives with no `pagination` object at all.
 *
 * The live RPCs accept any `p_limit`, but the page size MUST match the one the embedded
 * payload already used — see `pageLimitFor` below.
 */
const FALLBACK_PAGE_LIMIT = 25;

/** Rows shown in the collapsed state (spec v2 §7: "Top five"). */
const COLLAPSED_ROWS = 5;

/**
 * The page size to request from the live RPC.
 *
 * This has to be the EMBEDDED payload's own limit, not a constant of our choosing. Page 1
 * on screen is the payload's page 1, and the pager's page numbers are the payload's
 * `total_pages`. Requesting a different size renumbers the result set underneath a pager
 * that is still labelled with the old numbering: with an embedded limit of 25 and a
 * request of 50, "Next" from rows 1–25 returns rows 51–100 and rows 26–50 become
 * unreachable — and `total_pages` halves under the control the reader is looking at.
 *
 * Verified against the live capture (2026-08-13): `top_queries` 1712 items at limit 25
 * (69 pages), `top_pages` 78 at 25 (4 pages), `query_page_map` 3051 at 25 (123 pages).
 */
const pageLimitFor = (pagination) => pagination?.limit ?? FALLBACK_PAGE_LIMIT;

/**
 * A clickable value in the map — a search term or a page path.
 *
 * No underline. A column of underlined URLs reads as a wall of links and makes the paths
 * themselves harder to scan, which is the one thing this column exists for; hover shifts
 * the colour instead, and the whole row lights up with it.
 */
const FilterButton = ({ label, title, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="text-left text-[#32B1FF] hover:text-[#1a86c9] dark:hover:text-[#7fd0ff] transition-colors break-words no-underline"
  >
    {label}
  </button>
);

/**
 * Evidence for `top_queries`, `top_pages` and `query_page_map` — the three panels that
 * share `{ summary, items, pagination }` and have their own paging RPC.
 *
 * ── Two data sources, deliberately ──────────────────────────────────────────────
 * The panel payload carries page 1 (25 rows in the live capture) for the page's fixed
 * 90-day window. The Detail RPCs serve any page for a caller-supplied range, at a page
 * size this component takes from the embedded payload rather than choosing. So:
 *
 *   - first render  → the embedded rows, no RPC (§5: one page RPC for the page)
 *   - any page change, range change or filter change → live RPC from then on
 *
 * A range change resets to page 1 AND fetches it live. Without the fetch, page 1 would
 * still be the 90-day window's rows while page 2 came from the new range — two windows
 * in one table with nothing on screen saying so.
 *
 * ── §8 lives here ───────────────────────────────────────────────────────────────
 * `top_queries.summary.total_clicks` is LOWER than `search_headline.facts.clicks` by
 * design: Google withholds rare search terms, so the per-query rollup can never sum to
 * the site total. The summary line therefore never sits next to the headline number and
 * the difference is never computed or labelled.
 *
 * ── Exact vs substring (backend addendum §1, spec v2 §10) ───────────────────────
 * `p_exact` is the difference between "someone TYPED into a filter box" and "someone
 * CLICKED a row". A typed box is a substring search and stays that way — that is what
 * the reader means by typing three letters. A click means "this exact search", and
 * substring matching there is simply wrong: on Verde, clicking "camp verde" with
 * substring matching returned 490 different searches across 39 pages; exact returns the
 * one search across 10.
 *
 * ── What the map does NOT say (spec v2 §7) ──────────────────────────────────────
 * Several pages appearing for one search is NOT evidence that they compete. Search
 * Console credits an impression to every page in a sitelink block, so the usual cause is
 * that they appeared TOGETHER as one result — Savannah Lakes shows six pages at 12% each
 * for its own name, which is one sitelink block, not six pages fighting. The data cannot
 * distinguish that from real competition, so this component never claims either.
 *
 * The panel therefore states the relationship and stops: these pages appeared for this
 * search. Which of them competed, if any, is not in the data, and no arrangement of the
 * rows can put it there. The nesting below shows the reader that same fact — one search,
 * several pages — without asserting a cause for it.
 *
 * A `summary.pages_compete` flag once carried the claim and has since been removed from
 * `fn_get_gsc_query_pages`. Nothing here reads it, and nothing should if it returns: the
 * rule is about what the numbers can support, not about which fields arrive.
 */

/**
 * Export columns — see `lib/csv.js`.
 *
 * The headers are the table's OWN column labels, word for word. A spreadsheet that names
 * its columns differently from the screen they were copied off forces the reader to
 * re-learn them, and "Impressions (showed up in searches)" was doing exactly that.
 *
 * Figures stay raw so the spreadsheet can sort and sum them; the search term is cased to
 * match what was on screen.
 */
const QUERY_CSV = { header: 'Search', get: (r) => fmtSearchTerm(r.query) };
const PAGE_CSV = { header: 'Page', get: (r) => r.page };
const METRIC_CSV = [
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: 'Visits', get: (r) => r.clicks },
  { header: 'Click rate', get: (r) => r.ctr },
  { header: 'Position', get: (r) => r.position },
];

/** Export filenames read as what the reader saw, never as the database key. */
const PANEL_NAMES = {
  top_queries: 'What people searched',
  top_pages: 'Which pages they landed on',
  query_page_map: 'Which search led to which page',
};

const CSV_FOR = {
  top_queries: [QUERY_CSV, ...METRIC_CSV],
  top_pages: [PAGE_CSV, ...METRIC_CSV],
  query_page_map: [QUERY_CSV, PAGE_CSV, ...METRIC_CSV],
};

/**
 * Group query→page pairs by search, preserving first-appearance order (spec v2 §7).
 *
 * "query_page_map is a relationship, not a flat table. Group by search and nest its pages
 * underneath, rather than repeating the search down column one."
 *
 * Insertion order is the payload's order and is never re-sorted. A `Map` preserves it;
 * a plain object would not, because keys that look like array indices are reordered.
 *
 * @param {Array<object>} rows query→page pairs in payload order
 * @returns {Array<{query: string, pages: Array<object>}>} one entry per distinct search
 */
export const groupByQuery = (rows) => {
  const groups = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = row?.query ?? '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.entries()].map(([query, pages]) => ({ query, pages }));
};

/** Identity of a row across two date ranges — a map pair is (search, page). */
const rowIdentity = (panel, row) => {
  if (panel === 'top_queries') return row?.query ?? '';
  if (panel === 'top_pages') return row?.page ?? '';
  return `${row?.query ?? ''}|${row?.page ?? ''}`;
};

/** Compare two rows on one field. Missing values sort last in both directions. */
const compareOn = (key, dir) => (a, b) => {
  const av = a?.[key];
  const bv = b?.[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const result = typeof av === 'string' && typeof bv === 'string'
    ? av.localeCompare(bv)
    : Number(av) - Number(bv);
  return dir === 'asc' ? result : -result;
};

/**
 * One search and the pages it led to, nested underneath it.
 *
 * The search heading is itself the filter control. Grouping removed the repeated search
 * column that used to carry the click target, and the bidirectional filter §7 asks for
 * still needs a way in from the search side — so the heading takes that job. Clicking it
 * sends `p_exact: true` (addendum §1), because a click means "this exact search".
 */
/**
 * The column template every page row and the list header share, so the figures line up
 * down the whole list — across groups, not just within one. The page column is
 * `minmax(0,1fr)`, which lets a long UTM query string wrap INSIDE its own column rather
 * than pushing the numbers off to the right.
 */
const PAGE_COLS = 'grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_7rem_5rem_4.5rem] gap-x-4';

/**
 * Small print naming each figure. Rendered ONCE above the list, not per group.
 *
 * Repeating it under every search turned the panel into a wall of identical lines with no
 * way to see where one search's pages ended and the next began — every row looked like a
 * header and every header looked like a row. The groups carry their own outline instead
 * (see `QueryGroup`), and the column names are stated once at the top like any table.
 *
 * Hidden below `sm`, where four columns do not fit; each value carries its own inline
 * label there instead — the same trade the shared `responsive-card-table` CSS makes.
 */
const PageMetricsHeader = () => (
  <div className={`${PAGE_COLS} hidden sm:grid px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400`}>
    <span>Page</span>
    <span className="text-right">Showed up in searches</span>
    <span className="text-right">Visits</span>
    <span className="text-right">Position</span>
  </div>
);

/** A figure with a label that only shows where the header cannot. */
const PageMetric = ({ label, value }) => (
  <span className="sm:text-right tabular-nums text-gray-700 dark:text-gray-300">
    <span className="sm:hidden text-gray-400 dark:text-gray-500">{label} </span>
    {value}
  </span>
);

/**
 * One search and the pages it led to, drawn as a bounded card.
 *
 * The outline is the point. §7 asks for the map to read as a relationship — "group by
 * search and nest its pages underneath" — and a bare indent did not carry that: with
 * eight searches on screen the panel was one undifferentiated column of lines, and the
 * reader could not tell which pages belonged to which search without counting.
 *
 * The search sits in a tinted strip at the top of its own block; its pages sit inside,
 * separated by hairlines. Two levels of structure, so the grouping survives a glance.
 *
 * The search heading is also the filter control. Grouping removed the repeated search
 * column that used to carry the click target, and the bidirectional filter §7 asks for
 * still needs a way in from the search side — so the heading takes that job. Clicking it
 * sends `p_exact: true` (addendum §1), because a click means "this exact search".
 */
const QueryGroup = ({ group, onPickQuery, onPickPage }) => (
  <li className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
    {/* The LABEL is cased for display; the handler still carries the raw term, because
        `p_query` now matches by equality and a recased string would return nothing. */}
    {/* The heading is deliberately NOT link-blue. The page paths below it are blue, and
        a blue heading over blue rows gave the block no top — everything read as one list
        of links. The heading takes the strong text colour and shifts to brand on hover,
        so it still announces itself as a control. */}
    <div className="bg-gray-100 dark:bg-gray-800 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">
        {onPickQuery
          ? (
            <button
              type="button"
              onClick={() => onPickQuery(group.query)}
              className="text-left text-gray-900 dark:text-gray-100 hover:text-[#32B1FF] dark:hover:text-[#32B1FF] transition-colors break-words"
            >
              {fmtSearchTerm(group.query)}
            </button>
          )
          : fmtSearchTerm(group.query)}
      </p>
    </div>
    <ul className="divide-y divide-gray-100 dark:divide-gray-800/60 text-sm">
      {group.pages.map((row, i) => (
        <li
          key={`${row.page}-${i}`}
          className={`${PAGE_COLS} items-baseline px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors`}
        >
          <span className="min-w-0 break-words">
            <FilterButton label={pagePath(row.page)} title={row.page} onClick={() => onPickPage(row.page)} />
          </span>
          <PageMetric label="Showed up in searches" value={fmtCount(row.impressions)} />
          <PageMetric label="Visits" value={fmtCount(row.clicks)} />
          <PageMetric label="Position" value={fmtPosition(row.position)} />
        </li>
      ))}
    </ul>
  </li>
);

/** The groups, under one shared column header. */
const QueryGroupList = ({ groups, onPickQuery, onPickPage }) => (
  // The testid spans the header AND the groups, because they are one control together:
  // the column names at the top are what the figures inside the groups are named by.
  <div data-testid="query-page-groups">
    <PageMetricsHeader />
    <ul className="space-y-3">
      {groups.map((group) => (
        <QueryGroup
          key={group.query}
          group={group}
          onPickQuery={onPickQuery}
          onPickPage={onPickPage}
        />
      ))}
    </ul>
  </div>
);

const DetailTable = ({ panel, facts, sentence, clientId, detailRange, compareRange, collapsed = true }) => {
  const embedded = useMemo(
    () => ({ items: Array.isArray(facts?.items) ? facts.items : [], pagination: facts?.pagination || null }),
    [facts],
  );

  const [live, setLive] = useState(null); // null = still showing the embedded page
  // Which filter the response in `live` actually answered. A response outlives the
  // request that produced it — a later request can fail and leave the earlier body on
  // screen — so anything read out of it that DESCRIBES the request, rather than the
  // rows, has to be able to tell whether it is still current. See `exactApplied`.
  const [liveKey, setLiveKey] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // `exact` distinguishes a CLICKED row from a TYPED box. It travels with the filter
  // because the two are set together and must never drift apart.
  const [filter, setFilter] = useState({ query: null, pageUrl: null, exact: false });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sort, setSort] = useState(null);
  const [series, setSeries] = useState(null);
  const [compare, setCompare] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const hasFilter = Boolean(filter.query || filter.pageUrl);
  const rangeReady = Boolean(detailRange?.from && detailRange?.to);
  const rangeKey = `${detailRange?.from || ''}|${detailRange?.to || ''}`;
  const filterKey = `${filter.query || ''}|${filter.pageUrl || ''}|${filter.exact ? 'x' : 's'}|${appliedSearch}`;
  const pageLimit = pageLimitFor(embedded.pagination);
  const isMap = panel === 'query_page_map';

  const fetchLive = useCallback(async (targetPage, range) => {
    const window = range || detailRange;
    const params = {
      startDate: window?.from,
      endDate: window?.to,
      page: targetPage,
      limit: pageLimit,
    };
    // A typed box is a substring search across everything the range admits — this is
    // §14's "full-history search". `exact` is false for it by construction.
    if (panel === 'top_queries') return getQueries(clientId, { ...params, search: appliedSearch || null, exact: false });
    if (panel === 'top_pages') return getPages(clientId, { ...params, search: appliedSearch || null, exact: false });
    return getQueryPages(clientId, {
      ...params,
      query: filter.query,
      pageUrl: filter.pageUrl,
      exact: filter.exact,
    });
  }, [panel, clientId, detailRange, pageLimit, filter.query, filter.pageUrl, filter.exact, appliedSearch]);

  // Every in-flight request carries a sequence number. A response whose number is no
  // longer current is dropped: two fast "Next" clicks can otherwise land page 2 after
  // page 3 and win, leaving the table showing page 2 while the pager reads 3. The
  // unmount effect below bumps the counter so a late response can't call setState on a
  // component that is gone.
  const reqId = useRef(0);
  useEffect(() => () => { reqId.current += 1; }, []);

  const requestPage = useCallback((targetPage) => {
    if (!clientId || !rangeReady) return;
    const id = reqId.current + 1;
    reqId.current = id;
    // Read now, not in the callback: this is the filter the request going out describes,
    // and by the time it resolves the reader may have moved to another one.
    const forKey = filterKey;
    setLoading(true);
    setError('');
    fetchLive(targetPage)
      .then((resp) => {
        if (reqId.current !== id) return;
        setLive(resp ?? { items: [], pagination: null, summary: null });
        setLiveKey(forKey);
        setPage(targetPage);
      })
      .catch((e) => {
        // Logged raw for whoever is debugging, shown friendly to whoever is reading.
        // eslint-disable-next-line no-console
        console.error('[search-console] detail RPC failed', e);
        if (reqId.current !== id) return;
        setError(sanitizePanelError(e?.message) || 'Something went wrong loading these rows.');
      })
      .finally(() => { if (reqId.current === id) setLoading(false); });
  }, [clientId, rangeReady, fetchLive, filterKey]);

  // A range or filter change invalidates the current page number — page 7 of the old
  // result set is meaningless in the new one — so both reset to page 1.
  //
  // Whether page 1 then comes from the embedded payload or from a live call turns on ONE
  // question: is the range still the one the payload's rows were computed for? The first
  // usable range the page hands down is adopted as that baseline (`baselineRange`), and
  // while the reader has not moved it the embedded rows are correct and no call is made
  // — which is what keeps the initial render to a single page-level RPC.
  //
  // The moment the range differs, the embedded rows describe a window the reader is no
  // longer asking about, so page 1 must be fetched live for EVERY panel. Keying this off
  // `hasFilter` (as it once did) made the range control inert on `top_queries` and
  // `top_pages`, which have no filter buttons at all: the picker moved, the table kept
  // showing the old window, and a later "Next" then fetched page 2 of the NEW range —
  // two windows in one table with nothing on screen saying so.
  const baselineRange = useRef(null);
  const lastKeys = useRef(null);
  useEffect(() => {
    if (!rangeReady) return;

    // Adopt the first usable range without fetching: `detailRange` starts as
    // `{from: null, to: null}` and only becomes real once the dates RPC resolves, so the
    // null → real transition is not the reader moving anything.
    if (baselineRange.current === null) {
      baselineRange.current = rangeKey;
      lastKeys.current = `${rangeKey}::${filterKey}`;
      return;
    }

    const keys = `${rangeKey}::${filterKey}`;
    if (lastKeys.current === keys) return;
    lastKeys.current = keys;

    setPage(1);
    if (rangeKey === baselineRange.current && !hasFilter && !appliedSearch) {
      setLive(null);
      setLiveKey(null);
      return;
    }
    requestPage(1);
  }, [rangeKey, filterKey, rangeReady, hasFilter, appliedSearch, requestPage]);

  const source = live || embedded;
  const rawRows = useMemo(() => (Array.isArray(source.items) ? source.items : []), [source]);
  const pagination = source.pagination || null;

  // ── The `exact_match` echo (backend addendum §1) ──────────────────────────────
  // The response says which matching mode the database ACTUALLY applied. Labelling the
  // filter chip from that rather than from the local flag is the entire point of an
  // echo: when the two disagree the reader is told what happened, instead of a screen
  // that stays reassuring because it is describing the click rather than the query.
  //
  // Read from the LIVE response only. The embedded payload answers the page-level call,
  // which carries no filter at all, so its echo would describe a request the reader
  // never made. Absent — an older backend, or a panel whose RPC does not publish it —
  // falls back to the flag that was sent, which is the behaviour that shipped.
  //
  // And only while it still answers the CURRENT filter. A failed request leaves the
  // previous response in place: filter by query, land a response, then click a page and
  // have that request fail, and the chip would otherwise report the earlier query's mode
  // beside an error banner. An echo exists to surface a divergence, so a stale one is
  // wrong in precisely the case it was written for — fall back rather than mislead.
  const echoedExact = liveKey === filterKey ? live?.summary?.exact_match : undefined;
  const exactApplied = typeof echoedExact === 'boolean' ? echoedExact : filter.exact;

  const rows = useMemo(
    () => (sort ? [...rawRows].sort(compareOn(sort.key, sort.dir)) : rawRows),
    [rawRows, sort],
  );

  // ── Per-row sparklines (§10) ──────────────────────────────────────────────────
  // "p_values: the exact rows on screen. One call backs a whole table page." Only fetched
  // once the card is expanded — an owner never sees these, and §5 keeps the initial load
  // to one RPC. A failure leaves the column empty rather than surfacing an error: the
  // sparkline is decoration on rows whose numbers are already beside it.
  const seriesKind = panel === 'top_pages' ? 'page' : 'query';
  const seriesValues = useMemo(() => {
    if (collapsed || isMap) return [];
    const field = seriesKind === 'page' ? 'page' : 'query';
    return [...new Set(rows.map((r) => r?.[field]).filter(Boolean))];
  }, [collapsed, isMap, rows, seriesKind]);
  // The effect below needs a STABLE primitive to depend on, because `seriesValues` is a
  // fresh array every render. It must not be a lossy encoding of the array: query strings
  // contain spaces, so a space-joined key cannot be split back into the original values —
  // "camp verde" would be requested as two searches that do not exist. The key is used
  // only for change detection; the array itself is what gets sent.
  const seriesKey = JSON.stringify(seriesValues);

  useEffect(() => {
    if (!clientId || !rangeReady || seriesKey === '[]') {
      setSeries(null);
      return undefined;
    }
    let cancelled = false;
    getSeries(clientId, {
      startDate: detailRange.from,
      endDate: detailRange.to,
      kind: seriesKind,
      // Parsed back out of the key rather than closed over from `seriesValues`: the array
      // is a new identity on every render, and depending on it would refetch forever.
      // JSON round-trips the values exactly — a space-joined key would not, because a
      // search term contains spaces ("camp verde" would become two searches).
      values: JSON.parse(seriesKey),
    })
      .then((resp) => { if (!cancelled) setSeries(resp?.series ?? null); })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[search-console] series RPC failed', e);
        if (!cancelled) setSeries(null);
      });
    return () => { cancelled = true; };
  }, [clientId, rangeReady, detailRange?.from, detailRange?.to, seriesKind, seriesKey]);

  // ── Comparison range (§10) ────────────────────────────────────────────────────
  // "Two ranges side by side is two calls and a diff — nothing more is needed." The
  // second call is the SAME RPC with different bounds, and the difference §10 sanctions
  // is a subtraction of two published counts — the one piece of arithmetic on this page
  // the spec asks for by name. Both counts stay on screen beside it, so the reader can
  // always see what was subtracted from what.
  const compareFrom = compareRange?.from;
  const compareTo = compareRange?.to;
  useEffect(() => {
    if (collapsed || !compareFrom || !compareTo || !clientId) {
      setCompare(null);
      setCompareLoading(false);
      return undefined;
    }
    let cancelled = false;
    // A comparison-range change refetches the prior-period rows; drive the same refresh
    // bar the primary range does, so moving Compare from/to isn't a silent reload.
    setCompareLoading(true);
    fetchLive(1, { from: compareFrom, to: compareTo })
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp?.items) ? resp.items : [];
        const map = new Map();
        items.forEach((r) => map.set(rowIdentity(panel, r), r));
        setCompare(map);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[search-console] comparison RPC failed', e);
        if (!cancelled) setCompare(null);
      })
      .finally(() => { if (!cancelled) setCompareLoading(false); });
    return () => { cancelled = true; };
  }, [collapsed, compareFrom, compareTo, clientId, panel, fetchLive]);

  const applyQueryFilter = (q) => setFilter({ query: q, pageUrl: null, exact: true });
  const applyPageFilter = (url) => setFilter({ query: null, pageUrl: url, exact: true });
  const clearFilter = () => setFilter({ query: null, pageUrl: null, exact: false });

  const onSort = (key) => setSort((prev) => (
    prev?.key === key
      ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' }
  ));

  const sparklineColumn = {
    key: 'sparkline',
    label: 'Trend',
    sortable: false,
    render: (r) => {
      const key = seriesKind === 'page' ? r.page : r.query;
      return <Sparkline points={series?.[key]} label={`Visits over time for ${key}`} />;
    },
  };

  const compareColumns = [
    {
      key: 'compare_clicks',
      label: 'Visits, compared period',
      align: 'right',
      sortable: false,
      render: (r) => fmtCount(compare?.get(rowIdentity(panel, r))?.clicks),
    },
    {
      key: 'compare_delta',
      label: 'Difference',
      align: 'right',
      sortable: false,
      render: (r) => {
        const then = Number(compare?.get(rowIdentity(panel, r))?.clicks);
        const now = Number(r.clicks);
        // No row on the other side means the search did not appear in that period —
        // which is not the same as zero visits, and rendering it as "+N" would assert
        // growth from a baseline that was never measured.
        if (!Number.isFinite(then) || !Number.isFinite(now)) return '';
        const delta = now - then;
        if (delta === 0) return '0';
        return `${delta > 0 ? '+' : '−'}${fmtCount(Math.abs(delta))}`;
      },
    },
  ];

  const queryColumn = {
    key: 'query',
    label: 'Search',
    // The search term is the row's subject, but it reads at the same weight as the figures
    // beside it — the row is uniform, no bolded lead column.
    render: (r) => (isMap
      ? <FilterButton label={fmtSearchTerm(r.query)} onClick={() => applyQueryFilter(r.query)} />
      : <span className="font-normal text-gray-900 dark:text-gray-100">{fmtSearchTerm(r.query)}</span>),
  };

  const pageColumn = {
    key: 'page',
    label: 'Page',
    title: (r) => r.page,
    render: (r) => (isMap
      ? <FilterButton label={pagePath(r.page)} title={r.page} onClick={() => applyPageFilter(r.page)} />
      : pagePath(r.page)),
  };

  const metricColumns = [
    { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
    { key: 'clicks', label: 'Visits', align: 'right', render: (r) => fmtCount(r.clicks) },
    { key: 'ctr', label: 'Click rate', align: 'right', render: (r) => fmtRatioPct(r.ctr) },
    { key: 'position', label: 'Position', align: 'right', render: (r) => fmtPosition(r.position) },
  ];

  const baseColumns = panel === 'top_queries'
    ? [queryColumn, ...metricColumns]
    : panel === 'top_pages'
      ? [pageColumn, ...metricColumns]
      : [queryColumn, pageColumn, ...metricColumns];

  const columns = [
    ...baseColumns,
    ...(!isMap ? [sparklineColumn] : []),
    ...(compare ? compareColumns : []),
  ];

  // ── Collapsed (§7) ────────────────────────────────────────────────────────────
  // `top_queries` / `top_pages`: top five. `query_page_map`: one search with its pages
  // nested — the relationship shape, at the smallest size that still shows it is one.
  if (collapsed) {
    if (isMap) {
      const first = groupByQuery(rawRows)[0];
      return (
        // `relative` anchors the RefetchBar; the content dims while a range change
        // refetches, matching the paginated tables' refresh treatment.
        <div className="relative">
          {loading && <RefetchBar />}
          <div className={`space-y-2${loading ? ' opacity-60 transition-opacity' : ''}`}>
            {first ? (
              <QueryGroupList
                groups={[first]}
                onPickQuery={applyQueryFilter}
                onPickPage={applyPageFilter}
              />
            ) : (
              <NoRecords />
            )}
            {/* Deliberately NOT "these pages are competing" — see the header comment. */}
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Google can list several of your pages for one search, often together as a single
              result. Open this card to explore every search and page.
            </p>
          </div>
        </div>
      );
    }

    return (
      <PaginatedTable
        columns={baseColumns}
        rows={rawRows.slice(0, COLLAPSED_ROWS)}
        rowKey={(r, i) => `${r.query || ''}-${r.page || ''}-${i}`}
        label={panel === 'top_pages' ? 'pages' : 'searches'}
        ariaLabel={sentence}
        loading={loading}
      />
    );
  }

  // ── Expanded ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {isMap && !hasFilter && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select a search to see every page it leads to, or a page to see every search that
          lands there.
        </p>
      )}

      {/* A TYPED box is a substring search — `p_exact` stays false, which is what the
          reader means by typing three letters. Only a row CLICK sends `p_exact: true`. */}
      {!isMap && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); setAppliedSearch(search.trim()); }}
        >
          <label htmlFor={`gsc-search-${panel}`} className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {panel === 'top_pages' ? 'Find a page' : 'Find a search'}
          </label>
          {/* Wide enough to hold a real search phrase. The box is where a specialist types
              something like "pet friendly rv park sedona"; at its old content width they
              were typing into a slot that showed four words at a time. */}
          <input
            id={`gsc-search-${panel}`}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Contains…"
            className="w-full sm:w-80 md:w-96 text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
          />
          {/* A solid button, not a text link — it is the submit action for the field
              beside it, and the house styles a primary action this way everywhere else. */}
          <button
            type="submit"
            // `border border-transparent` so the button box matches the bordered input
            // beside it exactly — without it the two sit 2px apart in height.
            className="px-4 py-1.5 rounded-lg border border-transparent bg-[#32B1FF] text-white text-sm font-medium hover:bg-[#259ae6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900"
          >
            Find
          </button>
          {appliedSearch && (
            <button
              type="button"
              onClick={() => { setSearch(''); setAppliedSearch(''); }}
              className="px-2.5 py-1.5 rounded-lg border border-transparent text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Clear
            </button>
          )}
        </form>
      )}

      {hasFilter && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span data-testid="gsc-filter-chip" className="rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-gray-700 dark:text-gray-300 break-words">
            {/* A row click asks for equality, so the label normally reads "Search:"
                rather than "Searches matching:" — but it is the backend's echo that
                settles which, not the click. */}
            {filter.query
              ? `${exactApplied ? 'Search' : 'Searches matching'}: ${fmtSearchTerm(filter.query)}`
              : `${exactApplied ? 'Page' : 'Pages matching'}: ${pagePath(filter.pageUrl)}`}
          </span>
          <button type="button" onClick={clearFilter} className="text-[#32B1FF] hover:underline font-medium">
            Clear
          </button>
        </div>
      )}

      {error && <ErrorState variant="inset" message={error} onRetry={() => requestPage(page)} />}

      {/* The pager is only wired up when a range is available to page within. If
          `fn_get_search_console_dates` failed, `detailRange` never resolves and
          `requestPage` early-returns — so offering a "Next" that silently does nothing
          is worse than offering none. Say why instead. */}
      {!rangeReady && pagination && pagination.total_pages > 1 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing the first {fmtCount(rows.length)} rows. Paging is unavailable until the
          date range loads.
        </p>
      )}

      {isMap ? (
        // The map stays a relationship whether or not it is filtered — grouped and
        // nested, never a flat table, and never labelled as competition.
        <QueryGroupList
          groups={groupByQuery(rows)}
          // Once filtered to one exact search there is nothing left to narrow to, so the
          // heading stops being a control rather than becoming a no-op.
          onPickQuery={filter.query ? undefined : applyQueryFilter}
          onPickPage={applyPageFilter}
        />
      ) : (
        <PaginatedTable
          columns={columns}
          rows={rows}
          rowKey={(r, i) => `${r.query || ''}-${r.page || ''}-${i}`}
          pagination={pagination}
          onPageChange={rangeReady ? requestPage : undefined}
          loading={loading || compareLoading}
          label={panel === 'top_pages' ? 'pages' : panel === 'top_queries' ? 'searches' : 'pairs'}
          ariaLabel={sentence}
          sort={sort}
          onSort={onSort}
        />
      )}

      {!isMap && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Each trend line is scaled to its own row, so their heights are not comparable to
          each other.
        </p>
      )}

      <CsvButton
        panelKey={panel}
        name={PANEL_NAMES[panel]}
        columns={CSV_FOR[panel] || CSV_FOR.top_queries}
        rows={rows}
        range={detailRange}
      />
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * For `top_queries` / `top_pages`, collapsed is the top five with no controls at all;
 * expanded adds the substring search box, sortable headers, per-row sparklines, the
 * pager and the CSV. For `query_page_map`, collapsed is the FIRST search group only;
 * expanded is every group, each with a clickable heading that filters exactly.
 *
 * In all three cases the deciding factor is the same: rows. A panel that arrived with an
 * empty `items` array has nothing behind it, and offering "Show more" on it would open
 * onto a search box over no data.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
DetailTable.hasDepth = (facts) => Array.isArray(facts?.items) && facts.items.length > 0;

export default DetailTable;
